import { IonButton } from '@ionic/react';
import { useState, type ChangeEvent } from 'react';
import { useMessages } from '../i18n/LanguageProvider';
import type { AppData } from '../state/app-data';
import { useAppData } from '../state/AppDataProvider';
import { backupFileName, exportBackup, importBackup, type BackupError } from '../state/backup';
import { downloadJson } from './download';
import { backupErrorCode } from './settings-view';

type Status = 'restored' | BackupError['code'] | undefined;

function RestoreQuestion({ onYes, onNo }: { onYes: () => void; onNo: () => void }) {
  const { t } = useMessages();
  return (
    <div className="app-hint">
      <p>{t.restoreQuestion}</p>
      <div className="app-actions">
        <IonButton onClick={onYes}>{t.yes}</IonButton>
        <IonButton fill="outline" onClick={onNo}>
          {t.cancel}
        </IonButton>
      </div>
    </div>
  );
}

/**
 * DE: Sicherung in eine Datei schreiben und aus einer Datei wiederherstellen (nach Rückfrage).
 * EN: Write a backup to a file and restore one from a file (after a question).
 */
export function BackupSection() {
  const { data, dispatch, today } = useAppData();
  const { t } = useMessages();
  const [pending, setPending] = useState<AppData>();
  const [status, setStatus] = useState<Status>();
  const download = () => downloadJson(exportBackup(data), backupFileName(today));
  const choose = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setPending(importBackup(await file.text()));
      setStatus(undefined);
    } catch (error) {
      setStatus(backupErrorCode(error));
    }
  };
  const restore = (backup: AppData) => {
    dispatch({ type: 'restore-backup', data: backup });
    setPending(undefined);
    setStatus('restored');
  };
  return (
    <section className="app-card">
      <h2>{t.backupTitle}</h2>
      <p className="app-muted">{t.backupHint}</p>
      <div className="app-actions">
        <IonButton onClick={download}>{t.downloadBackup}</IonButton>
        <label className="app-file">
          <span>{t.restoreBackup}</span>
          <input
            type="file"
            accept="application/json,.json"
            aria-label={t.restoreBackup}
            onChange={(event) => void choose(event)}
          />
        </label>
      </div>
      {pending && (
        <RestoreQuestion onYes={() => restore(pending)} onNo={() => setPending(undefined)} />
      )}
      {status && (
        <p className="app-hint" role="status">
          {status === 'restored' ? t.restored : t.backupErrors[status]}
        </p>
      )}
    </section>
  );
}
